// @ts-nocheck
import { Component } from '@theme/component';

/**
 * @typedef {Object} TabChangeDetail
 * @property {number} number - Tab index (0-based)
 * @property {string|null} tabName - Tab name if available, null otherwise
 */

/**
 * Tab component that manages tab navigation with support for animations and accessibility.
 *
 * @extends {Component}
 */
export class TabsComponent extends Component {
  /**
   * @type {number}
   * @private
   */
  #activeTabIndex = 0;

  /**
   * @type {Map<string, number>}
   * @private
   */
  #panelNameToIndex = new Map();

  /**
   * @type {Map<number, string>}
   * @private
   */
  #indexToPanelName = new Map();

  /**
   * @type {Array<Function>}
   * @private
   */
  #eventListeners = [];

  /**
   * @type {Map<HTMLElement, {click: Function, keydown: Function}>}
   * @private
   */
  #headerEventListeners = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#setupDOMStructure();
    this.#initializeTabs();
    this.#setupAccessibility();
    this.#setupAnimation();
    this.#setupEventListeners();
    this.#openTab(0, false);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    this.#panelNameToIndex.clear();
    this.#indexToPanelName.clear();
    this.#eventListeners = [];
    this.removeAttribute('initialized');
  }

  /**
   * Opens a tab by index or name
   * @param {number|string} identifier - Tab index (number) or panel name (string)
   */
  openTab(identifier) {
    if (typeof identifier === 'number') {
      this.#openTab(identifier, true);
    } else if (typeof identifier === 'string') {
      const index = this.#panelNameToIndex.get(identifier);
      if (index !== undefined) {
        this.#openTab(index, true);
      } else {
        console.warn(`Tab with name "${identifier}" not found`);
      }
    }
  }

  /**
   * Sets the onTabChange callback
   * @param {function(Event, TabChangeDetail): void} callback - Callback function
   */
  set onTabChange(callback) {
    if (typeof callback === 'function') {
      this.#eventListeners.push({ type: 'change', callback });
    }
  }

  /**
   * Adds an event listener for tab changes
   * @param {string} eventType - Event type (currently only 'change' is supported)
   * @param {function(Event, TabChangeDetail): void} callback - Callback function
   */
  on(eventType, callback) {
    if (eventType === 'change' && typeof callback === 'function') {
      this.#eventListeners.push({ type: 'change', callback });
    }
  }

  /**
   * Removes an event listener
   * @param {string} eventType - Event type
   * @param {function} callback - Callback function to remove
   */
  off(eventType, callback) {
    this.#eventListeners = this.#eventListeners.filter(
      (listener) => listener.type !== eventType || listener.callback !== callback
    );
  }

  /**
   * Sets up the DOM structure by finding or creating required containers
   * @private
   */
  #setupDOMStructure() {
    // Find or create .tab-header
    let tabHeader = this.querySelector('.tab-header');
    if (!tabHeader) {
      tabHeader = document.createElement('div');
      tabHeader.className = 'tab-header';
      this.insertBefore(tabHeader, this.firstChild);
    } else {
      // Empty it if it exists
      tabHeader.innerHTML = '';
    }

    // Create .tab-buttons and .tab-actions
    const tabButtons = document.createElement('div');
    tabButtons.className = 'tab-buttons';
    tabHeader.appendChild(tabButtons);

    const tabActions = document.createElement('div');
    tabActions.className = 'tab-actions';
    tabHeader.appendChild(tabActions);

    // Find or create .tab-panels
    let tabPanels = this.querySelector('.tab-panels');
    if (!tabPanels) {
      tabPanels = document.createElement('div');
      tabPanels.className = 'tab-panels';
      this.appendChild(tabPanels);
    }

    // Find all ref="tab-button" that are not already in .tab-buttons and move them
    const tabButtonElements = this.querySelectorAll('[ref="tab-button"]');
    tabButtonElements.forEach((button) => {
      if (!tabButtons.contains(button)) {
        tabButtons.appendChild(button);
      }
    });

    // Find all ref="tab-action" that are not already in .tab-actions and move them
    const tabActionElements = this.querySelectorAll('[ref="tab-action"]');
    tabActionElements.forEach((action) => {
      if (!tabActions.contains(action)) {
        tabActions.appendChild(action);
      }
    });

    // Find all ref="tab-panel" that are not already in .tab-panels and move them
    const tabPanelElements = this.querySelectorAll('[ref="tab-panel"]');
    tabPanelElements.forEach((panel) => {
      if (!tabPanels.contains(panel)) {
        tabPanels.appendChild(panel);
      }
    });

    // Remove all children not tab-header and tab-panels
    const children = Array.from(this.children);
    children.forEach((child) => {
      if (!child.classList.contains('tab-header')  &&  !child.classList.contains('tab-panels')) {
        child.remove();
      }
    });
    // Store references for later use
    this.tabButtons = tabButtons;
    this.tabActions = tabActions;
    this.tabPanels = tabPanels;
    this.headerButtons = Array.from(tabButtons.children);
    this.panelContents = Array.from(tabPanels.children);
    this.actionButtons = Array.from(tabActions.children);
  }

  /**
   * Initializes the tab structure and mappings
   * @private
   */
  #initializeTabs() {
    // Build panel name to index mapping
    for (let i = 0; i < this.panelContents.length; i++) {
      const panel = this.panelContents[i];
      panel.classList.add('panel');
      const name = panel.getAttribute('name');

      if (name) {
        this.#panelNameToIndex.set(name, i);
        this.#indexToPanelName.set(i, name);
      }
    }

    // Set up header-to-panel associations
    for (let i = 0; i < this.headerButtons.length; i++) {
      const header = this.headerButtons[i];
      const forAttribute = header.getAttribute('for');

      if (forAttribute) {
        // Header specifies a panel by name
        const panelIndex = this.#panelNameToIndex.get(forAttribute);
        if (panelIndex !== undefined) {
          header.setAttribute('data-panel-index', panelIndex.toString());
        } else {
          console.warn(`Panel with name "${forAttribute}" not found for header at index ${i}`);
          // Fall back to position mapping
          header.setAttribute('data-panel-index', i.toString());
        }
      } else {
        // Map by position
        header.setAttribute('data-panel-index', i.toString());
      }
    }
  }

  /**
   * Sets up ARIA attributes for accessibility
   * @private
   */
  #setupAccessibility() {
    // Set up tablist on .tab-buttons
    if (this.tabButtons && this.headerButtons && this.headerButtons.length > 0) {
      this.tabButtons.setAttribute('role', 'tablist');
      if (!this.tabButtons.hasAttribute('aria-label') && !this.tabButtons.hasAttribute('aria-labelledby')) {
        this.tabButtons.setAttribute('aria-label', 'Tabs');
      }
    }

    // Set up each header as a tab
    for (let i = 0; i < this.headerButtons.length; i++) {
      const header = this.headerButtons[i];
      const panelIndex = parseInt(header.getAttribute('data-panel-index') || '0', 10);
      const panel = this.panelContents[panelIndex];

      if (!panel) continue;

      // Generate unique IDs
      const headerId = `tab-${this.#generateId()}-${i}`;
      const panelId = `panel-${this.#generateId()}-${panelIndex}`;

      header.id = headerId;
      panel.id = panelId;

      // Set ARIA attributes
      header.setAttribute('role', 'tab');
      header.setAttribute('aria-controls', panelId);
      header.setAttribute('aria-selected', 'false');
      header.setAttribute('tabindex', '-1');

      // Set panel ARIA attributes
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', headerId);
      panel.hidden = true;
    }
  }

  /**
   * Sets up animation configuration based on component attributes
   * @private
   */
  #setupAnimation() {
    const hasFlyIn = this.hasAttribute('fly-in');
    
    if (hasFlyIn) {
      // Setup scroll snap for fly-in animation
      this.tabPanels.style.display = 'flex';
      this.tabPanels.style.overflowX = 'hidden';
      this.tabPanels.style.scrollSnapType = 'x mandatory';
      this.tabPanels.style.scrollBehavior = 'smooth';
      this.tabPanels.style.scrollbarWidth = 'none';
      this.tabPanels.style.msOverflowStyle = 'none';
      
      // Hide webkit scrollbar
      const style = document.createElement('style');
      style.textContent = `
        .tab-panels::-webkit-scrollbar {
          display: none;
        }
      `;
      if (!document.head.querySelector(`style[data-tabs-scrollbar]`)) {
        style.setAttribute('data-tabs-scrollbar', '');
        document.head.appendChild(style);
      }
      
      // Setup scroll snap on each panel
      this.panelContents.forEach((panel) => {
        panel.style.scrollSnapAlign = 'start';
        panel.style.flexShrink = '0';
        panel.style.width = '100%';
        panel.hidden = false;
      });
    }
  }

  /**
   * Sets up event listeners for tab headers
   * @private
   */
  #setupEventListeners() {
    for (const header of this.headerButtons) {
      // Click handler
      const clickHandler = () => {
        const panelIndex = parseInt(header.getAttribute('data-panel-index') || '0', 10);
        this.#openTab(panelIndex, true);
      };
      header.addEventListener('click', clickHandler);

      // Keyboard navigation
      const keydownHandler = (event) => {
        this.#handleKeydown(event, header);
      };
      header.addEventListener('keydown', keydownHandler);

      // Store handlers for cleanup
      this.#headerEventListeners.set(header, { click: clickHandler, keydown: keydownHandler });
    }
  }

  /**
   * Handles keyboard navigation for tabs
   * @param {KeyboardEvent} event - Keyboard event
   * @param {HTMLElement} header - Current header element
   * @private
   */
  #handleKeydown(event, header) {
    const currentIndex = this.headerButtons.indexOf(header);

    let targetIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = event.key === 'ArrowLeft' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0) targetIndex = this.headerButtons.length - 1;
        if (targetIndex >= this.headerButtons.length) targetIndex = 0;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = this.headerButtons.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        const panelIndex = parseInt(header.getAttribute('data-panel-index') || '0', 10);
        this.#openTab(panelIndex, true);
        return;
      default:
        return;
    }

    if (targetIndex !== currentIndex) {
      this.headerButtons[targetIndex].focus();
      const panelIndex = parseInt(this.headerButtons[targetIndex].getAttribute('data-panel-index') || '0', 10);
      this.#openTab(panelIndex, true);
    }
  }

  /**
   * Opens a specific tab
   * @param {number} index - Panel index to open
   * @param {boolean} triggerEvent - Whether to trigger change event
   * @private
   */
  #openTab(index, triggerEvent) {
    if (index < 0 || index >= this.panelContents.length) {
      console.warn(`Tab index ${index} is out of range`);
      return;
    }

    const previousIndex = this.#activeTabIndex;
    this.#activeTabIndex = index;

    // Get the panel to show
    const targetPanel = this.panelContents[index];
    const hasFlyIn = this.hasAttribute('fly-in');
    const hasFloatIn = this.hasAttribute('float-in');

    // Determine animation direction
    const direction = index > previousIndex ? 'right' : 'left';

    if (hasFlyIn) {
      // Use scroll snap for fly-in animation
      this.#scrollToPanel(index);
      
      // All panels should be visible for scroll snap
      this.panelContents.forEach((panel) => {
        panel.hidden = false;
      });
    } else if (hasFloatIn) {
      // For float-in, animate out previous panel first, then animate in new panel
      const previousPanel = previousIndex >= 0 && previousIndex < this.panelContents.length 
        ? this.panelContents[previousIndex] 
        : null;
      
      if (previousPanel && previousPanel !== targetPanel && !previousPanel.hidden) {
        // Animate out previous panel
        this.#hidePanelFloatOut(previousPanel, () => {
          // After out animation completes, animate in new panel
          this.#showPanelFloatIn(targetPanel);
        });
      } else {
        // No previous panel to animate out, just show new panel
        this.#showPanelFloatIn(targetPanel);
      }
      
      // Hide all other panels without animation
      for (const panel of this.panelContents) {
        if (panel === targetPanel || panel === previousPanel) continue;
        panel.hidden = true;
        panel.style.position = 'absolute';
      }
    } else {
      // No animation - simple show/hide
      for (const panel of this.panelContents) {
        if (panel === targetPanel) continue;
        panel.hidden = true;
      }
      targetPanel.hidden = false;
    }

    // Update header states
    for (let i = 0; i < this.headerButtons.length; i++) {
      const header = this.headerButtons[i];
      const headerPanelIndex = parseInt(header.getAttribute('data-panel-index') || '0', 10);
      const isActive = headerPanelIndex === index;

      header.setAttribute('aria-selected', isActive.toString());
      header.setAttribute('tabindex', isActive ? '0' : '-1');
      header.classList.toggle('active', isActive);
    }

    // Update action states
    for (const action of this.actionButtons) {
      const actionPanelIndex = parseInt(action.getAttribute('data-panel-index') || '-1', 10);
      const actionPanelName = action.getAttribute('data-panel-name');
      const isActive = actionPanelIndex === index || actionPanelName === this.#indexToPanelName.get(index);
      action.setAttribute('aria-selected', isActive.toString());
      action.setAttribute('tabindex', isActive ? '0' : '-1');
      action.classList.toggle('active', isActive);
    }

    // Trigger change event
    if (triggerEvent) {
      this.#triggerChangeEvent(index);
    }
  }

  /**
   * Scrolls to a specific panel using scroll snap
   * @param {number} index - Panel index to scroll to
   * @private
   */
  #scrollToPanel(index) {
    const targetPanel = this.panelContents[index];
    if (!targetPanel) return;

    // Use offsetLeft for accurate positioning
    const scrollPosition = targetPanel.offsetLeft;

    this.tabPanels.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }

  /**
   * Shows a panel with float-in animation
   * @param {HTMLElement} panel - Panel element to show
   * @private
   */
  #showPanelFloatIn(panel) {
    // Set position relative for in animation
    panel.style.position = 'relative';
    panel.hidden = false;
    
    // Set initial state: translateY 20px, opacity 0
    panel.style.transform = 'translateY(20px)';
    panel.style.opacity = '0';
    
    let completed = false;
    
    const complete = () => {
      if (completed) return;
      completed = true;
      
      panel.style.transform = '';
      panel.style.opacity = '';
    };
    
    // Trigger animation to: translateY 0px, opacity 1
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.transform = 'translateY(0px)';
        panel.style.opacity = '1';
      });
    });
    
    // Remove transform after animation completes
    panel.addEventListener('transitionend', complete, { once: true });
    
    // Fallback timeout in case transitionend doesn't fire
    setTimeout(complete, 300);
  }

  /**
   * Hides a panel with float-out animation
   * @param {HTMLElement} panel - Panel element to hide
   * @param {Function} onComplete - Callback when animation completes
   * @private
   */
  #hidePanelFloatOut(panel, onComplete) {
    // Set initial state: translateY 0px, opacity 1
    panel.style.position = 'relative';
    panel.style.transform = 'translateY(0px)';
    panel.style.opacity = '1';
    
    let completed = false;
    
    const complete = () => {
      if (completed) return;
      completed = true;
      
      panel.hidden = true;
      panel.style.position = 'absolute';
      panel.style.transform = '';
      panel.style.opacity = '';
      if (onComplete) {
        onComplete();
      }
    };
    
    // Trigger animation to: translateY 20px, opacity 0
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.transform = 'translateY(20px)';
        panel.style.opacity = '0';
      });
    });
    
    // After animation completes: set hidden and position absolute, then call callback
    panel.addEventListener('transitionend', complete, { once: true });
    
    // Fallback timeout in case transitionend doesn't fire
    setTimeout(complete, 300);
  }

  /**
   * Triggers the tab change event
   * @param {number} index - Active tab index
   * @private
   */
  #triggerChangeEvent(index) {
    const tabName = this.#indexToPanelName.get(index) || null;
    const detail = { number: index, tabName };

    // Create and dispatch custom event
    const event = new CustomEvent('tab:change', {
      detail,
      bubbles: true,
      cancelable: true
    });

    this.dispatchEvent(event);

    // Call registered listeners
    for (const listener of this.#eventListeners) {
      if (listener.type === 'change') {
        try {
          listener.callback(event, detail);
        } catch (error) {
          console.error('Error in tab change listener:', error);
        }
      }
    }
  }

  /**
   * Removes all event listeners
   * @private
   */
  #removeEventListeners() {
    // Remove DOM event listeners from header buttons
    if (this.headerButtons) {
      for (const header of this.headerButtons) {
        const handlers = this.#headerEventListeners.get(header);
        if (handlers) {
          header.removeEventListener('click', handlers.click);
          header.removeEventListener('keydown', handlers.keydown);
        }
      }
    }
    
    // Clear stored handler references
    this.#headerEventListeners.clear();
    
    // Clear custom event listeners
    this.#eventListeners = [];
  }

  /**
   * Generates a unique ID for ARIA attributes
   * @returns {string} Unique identifier
   * @private
   */
  #generateId() {
    return Math.random().toString(36).substring(2, 9);
  }
}

customElements.define('tabs-component', TabsComponent);

